// packages/ui/src/sidebar/SidebarHost.tsx
import { useEffect, useRef, useSyncExternalStore, type MouseEvent, type ReactElement } from 'react';
import { SIDEBAR_MAX_WIDTH, SIDEBAR_MIN_WIDTH, useDocStore } from '../store/docStore';
import { useRegisterFocusZone } from '../focus';
import { SidebarPanelRegistry } from './SidebarPanelRegistry';
import { useSidebarStore } from './sidebarStore';

/**
 * Renders the active sidebar panel's body when the sidebar is open (collapsed →
 * renders nothing; the ActivityBar rail alone remains). Owns the sidebar's width
 * + drag-to-resize, and registers the 'sidebar' focus zone so the existing
 * focus-jump shortcuts (⌥⌘← / F6) land here.
 */
export function SidebarHost(): ReactElement | null {
  const panels = useSyncExternalStore(
    SidebarPanelRegistry.subscribe,
    SidebarPanelRegistry.getAll,
    SidebarPanelRegistry.getAll,
  );
  const open = useSidebarStore((s) => s.open);
  const activePanelId = useSidebarStore((s) => s.activePanelId);
  const width = useDocStore((s) => s.sidebarWidth);
  const setSidebarWidth = useDocStore((s) => s.setSidebarWidth);

  const asideRef = useRef<HTMLElement>(null);
  useRegisterFocusZone(asideRef, 'sidebar', '[data-focus-primary], button, [role="treeitem"]', {
    label: 'Left sidebar',
    order: 10,
    isVisible: () => useSidebarStore.getState().open,
  });

  // Drag-to-resize (ported from the former Sidebar).
  const dragState = useRef<{ startX: number; startW: number } | null>(null);
  useEffect(() => {
    function onMove(e: PointerEvent): void {
      const st = dragState.current;
      if (!st) return;
      setSidebarWidth(st.startW + (e.clientX - st.startX));
    }
    function onUp(): void {
      if (!dragState.current) return;
      dragState.current = null;
      document.body.style.removeProperty('cursor');
      document.body.style.removeProperty('user-select');
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [setSidebarWidth]);

  const startResize = (e: MouseEvent): void => {
    dragState.current = { startX: e.clientX, startW: width };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  if (!open) return null;
  const panel = panels.find((p) => p.id === activePanelId) ?? panels[0];
  if (!panel) return null;

  const Body = panel.Component;
  return (
    <aside
      className="mp-sidebarhost"
      style={{ width }}
      data-focus-zone="sidebar"
      aria-label={panel.title}
      ref={asideRef}
    >
      <Body active />
      <div
        className="mp-sidebar__resize"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize sidebar"
        aria-valuenow={width}
        aria-valuemin={SIDEBAR_MIN_WIDTH}
        aria-valuemax={SIDEBAR_MAX_WIDTH}
        onPointerDown={startResize}
      />
    </aside>
  );
}
